import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Box, FormControl, Select, MenuItem, Input, InputLabel, Chip, Button, LinearProgress } from '@material-ui/core';
import moment from "moment";
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';

import { makeStyles } from '@material-ui/core/styles';
import { ResponsiveContainer, Legend, AreaChart, LineChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Label, ComposedChart, Bar } from 'recharts';

import { stationListRequest } from '../actions/stationQueryAction';
import { smartQueryRequest } from '../actions/smartQueryAction';
import "moment/locale/zh-tw";

moment.locale('zh-tw');

const useStyles = makeStyles((theme) => ({
    select: {
        paddingTop: theme.spacing(2),
        backgroundColor: 'white'
    },
    selectMenu: {
        fontSize: '1rem',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
}));

const factors = {
    DCV: '直流電壓曲線',
    DCA: '直流電流曲線',
    DCW: '直流功率曲線',
    ACV: '交流電壓曲線',
    ACA: '交流電流曲線',
    ACW: '交流功率曲線',
    COFF: '直交流轉換率曲線',
    Temperature: '變流器溫度曲線',
    Energy: '每小時發電量曲線(同日為當日累積電量)',
    AvgRA: 'RA效率值曲線',
    AvgPR: 'PR效率值曲線',
    AvgDUST: '落塵量曲線',
};

const factors_unit = {
    DCV: 'V',
    DCA: 'A',
    DCW: 'W',
    ACV: 'V',
    ACA: 'A',
    ACW: 'KW',
    COFF: '%',
    Temperature: '°C',
    Energy: 'KW',
    AvgRA: '%',
    AvgPR: '%',
    AvgDUST: '%',
};

const colorMap = ['DEEPSKYBLUE', 'ROSYBROWN', 'YELLOWGREEN', 'MEDIUMPURPLE', 'GOLD', 'INDIANRED', 'CADETBLUE', 'MAROON', 'OLIVE', 'ROYALBLUE',
    'GRAY', 'TEAL', 'PURPLE', 'MAROON', 'SILVER', 'NAVY'];

const now = (new Date()).setHours(0, 0, 0);
var smartData = {};

const makeDaysHoursList = (selectInverter, startDate, endDate) => {
    smartData = [];
    if (startDate.diff(endDate, 'days') === 0) { 
        selectInverter.forEach( inv => {
            let data = [];
            let cur = moment(startDate).add(9, 'h');
            for (let i = 0; i < 7 * 60 + 1; i++) {
                    console.log(i, cur)
                    data.push({
                        refDate: moment(cur).format('YYYY-MM-DD'),
                        refHour: i,
                        unixTime: moment(cur).add(i, 'm').format('YYYY-MM-DD HH:mm'),
                    });
                }
            smartData[inv] = data;
        })        
    } else {
        selectInverter.forEach( inv => {
            let data = [];
            let cur = moment(startDate);
            while (cur <= endDate) {
                for (let i = 9; i < 17; i++) {
                    data.push({
                        refDate: moment(cur).format('YYYY-MM-DD'),
                        refHour: i,
                        unixTime: moment(cur).add(i, 'h').format('YYYY-MM-DD HH:mm')
                    });
                }
                cur.add(1, 'd');
            }

            smartData[inv] = data;
        })
    }
};

const SmartQuery = () => {
    const style = useStyles();
    const stationList = useSelector(state => state.stationList);
    const smartQuery = useSelector(state => state.smartQuery);
    const dispatch = useDispatch();
    const [stations, setStations] = useState([]);
    const [inverters, setInverters] = useState([]);
    const [selectStation, setSelectStation] = useState({
        station: -1,
        inverter: '',
    });
    const [selectColumn, setSelectColumn] = useState('Energy');
    const [selectInverter, setSelectInverter] = useState([]);
    const [selectStartDate, setSelectStartDate] = useState(moment().hour(0).minute(0).second(0));
    const [selectEndDate, setSelectEndDate] = useState(moment().hour(0).minute(0).second(0));
    const [minPyrano, setMinPyrano] = useState(0);
    const [maxPyrano, setMaxPyrano] = useState(0);
    const [minThermo, setMinThermo] = useState(0);
    const [maxThermo, setMaxThermo] = useState(0);

    useEffect(() => {
        if (stationList && stationList.stations && stationList.stations.length > 0) {
            setStations(stationList.stations);
            setInverters(stationList.stations[0].inverters);
            setSelectInverter([stationList.stations[0].inverters[0].IndexID]);
            setSelectStation((current) => {
                if (current.station === -1)
                    return { station: stationList.stations[0].ID, inverter: -1 }
                else
                    return current;
            });
        } else {
            if (!stationList || stationList.status === 0) {
                dispatch(stationListRequest());
            }
        };
    }, [stationList, dispatch]);

    const makeDrawData = useCallback(() => {

        let rec = smartQuery.result;
        if (rec.length === 0)
            return;

        if (selectStartDate.diff(selectEndDate, 'days') === 0) {
            rec.forEach( x => {
                let data = smartData[x.InverterID];
                let inverter = inverters.find(inv => (inv.IndexID === x.InverterID))
                let mppts = inverter ? inverter.mppts.length : 1;
                if (data) {
                    x.data.forEach( v => {
                        let item = data.find( f => moment(f.unixTime).format('YYYY-MM-DD HH:mm') === moment(v.CaptureTime).format('YYYY-MM-DD HH:mm') );
                        if (item) {
                            item.SUM = v.SUM;
                            item.TEMP = v.TEMP;
                            if (selectColumn === 'DCV' || selectColumn === 'ACV') {
                                item.VALUE = (v.VALUE * 10.0 / mppts) / 10;
                            } else {
                                item.VALUE = v.VALUE;
                            }
                        }
                    })
                }
            })
        } else {
            rec.forEach( x => {
                let data = smartData[x.InverterID];
                if (data) {
                    x.data.forEach( v => {
                        let item = data.find( f => f.refDate === v.refDate && f.refHour === v.refHour );
                        if (item) {
                            item.SUM = v.SUM;
                            item.TEMP = v.TEMP;
                            item.VALUE = v.VALUE;
                        }
                    })
                }
            })
        }
    }, [smartQuery]);

    useEffect(() => {
        if (smartQuery.status === 2 && smartQuery.result && smartQuery.result.length > 0) {
            makeDrawData();
        }
    }, [smartQuery, makeDrawData]);

    const handleStationChanged = (event) => {
        let new_station = stations.find(x => x.ID === event.target.value);
        if (new_station) {
            setInverters(new_station.inverters);
            setSelectStation({ station: new_station.ID, inverter: -1 });
            setSelectInverter([]);
        }
    };

    const handleInverterChanged = (event) => {
        setSelectInverter(event.target.value);
    };

    const handleDateChange = (type, date) => {
        if (type === 'start') {
            if (date <= selectEndDate)
                setSelectStartDate(date);
        } else {
            if (date >= selectStartDate)
                setSelectEndDate(date);
        }
    };

    const handleDataColumnChange = (event) => {
        setSelectColumn(event.target.value);
    };

    const handleFilterValueChange = (event) => {
        let value = parseInt(event.target.value, 10);
        if (isNaN(value)) return;

        if (event.target.name === 'minPyrano')
            setMinPyrano(value);
        else if (event.target.name === 'maxPyrano')
            setMaxPyrano(value);
        else if (event.target.name === 'minThermo')
            setMinThermo(value);
        else if (event.target.name === 'maxThermo')
            setMaxThermo(value);
    };

    const handleQueryClicked = (event) => {
        if (event)
            event.preventDefault();
        let params = {};

        if (selectInverter === null || selectInverter.length === 0) {
            window.alert("請選擇要查詢的機台 !");
            return;
        }

        params['start'] = moment(selectStartDate).format('YYYY-MM-DD');
        params['end'] = moment(selectEndDate).format('YYYY-MM-DD');
        params['column'] = selectColumn;
        params['inverters'] = selectInverter;
        params['pryanoMin'] = minPyrano;
        params['pyranoMax'] = maxPyrano;
        params['thermoMin'] = minThermo;
        params['thermoMax'] = maxThermo;

        makeDaysHoursList(selectInverter, selectStartDate, selectEndDate);
        dispatch(smartQueryRequest(selectStation.station, params));

    };

    const handleSelectAllClicked = (event) => {
        if (event)
            event.preventDefault();
        
            let inv = inverters.map((x) => (x.IndexID));
            setSelectInverter(inv);
    };

    const inDay = () => {
        return (moment(selectStartDate).format('YYYY-MM-DD') === moment(selectEndDate).format('YYYY-MM-DD'));
    }

    let queryDisable = !(stationList && stationList.stations && stationList.stations.length > 0) || (smartQuery.status === 1);

    return (
        <Box m={2}>
            <Grid container spacing={1}>
                <Grid item container spacing={2}>
                    <Grid item xs={12} sm={12} md={5}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="station-select-label" >案場</InputLabel>
                            <Select variant="filled" labelId="station-select-label" classes={{ selectMenu: style.selectMenu }}
                                value={selectStation.station} onChange={handleStationChanged}>
                                {stations ? stations.map((station, index) => (
                                    <MenuItem key={index} value={station.ID}>{`${station.ID} : ${station.Name}  (${station.Description})`}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="data-select-label" >資料類型</InputLabel>
                            <Select variant="filled" labelId="data-select-label" classes={{ selectMenu: style.selectMenu }} value={selectColumn}
                                onChange={handleDataColumnChange}>
                                {Object.keys(factors).map((item, index) => (
                                    <MenuItem key={index} dense value={item}>{factors[item]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <MuiPickersUtilsProvider utils={MomentUtils} locale={'zh-tw'}>
                            <KeyboardDatePicker margin="normal" id="start-date-picker-dialog"
                                label="開始日期" format="yyyy-MM-DD" variant="filled" value={selectStartDate}
                                style={{ padding: 0, margin: 0, backgroundColor: 'WHITESMOKE' }} onChange={date => handleDateChange('start', date)} />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <MuiPickersUtilsProvider utils={MomentUtils} locale={'zh-tw'}>
                            <KeyboardDatePicker margin="normal" id="end-date-picker-dialog"
                                label="結束日期" format="yyyy-MM-DD" variant="filled" value={selectEndDate}
                                style={{ padding: 0, margin: 0, backgroundColor: 'WHITESMOKE' }} onChange={date => handleDateChange('end', date)} />
                        </MuiPickersUtilsProvider>
                    </Grid>
                </Grid>
                <Grid item container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={12} md={9}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="inv-select-label" >機台</InputLabel>
                            <Select variant="filled" multiple labelId="inv-select-label" classes={{ selectMenu: style.selectMenu }} value={selectInverter}
                                onChange={handleInverterChanged} renderValue={(selected) => {
                                    if (selected) {
                                        return (
                                            <div className={style.chips}>
                                                {selected.map((value) => {
                                                    var inv = inverters.find(x => x.IndexID === value)
                                                    if (inv) {
                                                        return (
                                                            <Chip key={value} label={`${inv.IndexID} - ${inv.Name} (${inv.KW} KW)`}
                                                                style={{ margin: 0, backgroundColor: 'white', fontSize: '1rem' }} />)
                                                    } else {
                                                        return null;
                                                    }
                                                })}
                                            </div>
                                        )
                                    } else {
                                        return null;
                                    }
                                }} >
                                {inverters ? inverters.map((inverter, index) => (
                                    <MenuItem key={index} value={inverter.IndexID}>{`${inverter.IndexID} - ${inverter.Name} (${inverter.KW} KW)`}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={1}>
                        <Button variant="contained" color="primary" onClick={handleSelectAllClicked} disabled={queryDisable} >
                            全&nbsp;選
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12} md={1}>
                        <Button variant="contained" color="primary" fullWidth onClick={handleQueryClicked} disabled={queryDisable} >
                            查&nbsp;&nbsp;詢
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justify="center" style={{ marginTop: 10 }} spacing={2}>
                <Grid item xs={12} md={2} lg={2}>
                    <Box display="block" fontWeight="bold">日射計:(W/m2)</Box>
                    <Box display="flex">
                        <Input margin="dense" value={minPyrano} inputProps={{ 'aria-label': 'min' }} style={{ marginTop: 5, backgroundColor: 'WHITESMOKE' }}
                            name="minPyrano" onChange={(event) => handleFilterValueChange(event)} />&nbsp;&nbsp;~&nbsp;&nbsp;
                        <Input margin="dense" value={maxPyrano} inputProps={{ 'aria-label': 'max' }} style={{ marginTop: 5, backgroundColor: 'WHITESMOKE' }}
                            name="maxPyrano" onChange={(event) => handleFilterValueChange(event)} />
                    </Box>
                    <Box display="block" fontWeight="bold" mt={3}>溫度計:(°C)</Box>
                    <Box display="flex">
                        <Input margin="dense" value={minThermo} inputProps={{ 'aria-label': 'min' }} style={{ marginTop: 5, backgroundColor: 'WHITESMOKE' }}
                            name="minThermo" onChange={(event) => handleFilterValueChange(event)} />&nbsp;&nbsp;~&nbsp;&nbsp;
                        <Input margin="dense" value={maxThermo} inputProps={{ 'aria-label': 'max' }} style={{ marginTop: 5, backgroundColor: 'WHITESMOKE' }}
                            name="maxThermo" onChange={(event) => handleFilterValueChange(event)} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={10} lg={10}>
                    {stationList && stationList.status === 2 ?
                        smartQuery && smartQuery.status === 2 ?
                            <div style={{ textAlign: 'right', marginTop: 20, marginRight: 40, backgroundColor: 'rgba(255, 255, 255 , 0.8)', borderRadius: '10px', height: '70vh' }}>
                                {Object.keys(smartData).length > 0 ?
                                    <>
                                        <ResponsiveContainer height="100%">
                                            <ComposedChart   margin={{ top: 10, right: 50, left: 20, bottom: 50 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <Legend iconType="rect" verticalAlign="top" height={60} />
                                                <XAxis label={{ value: '時間', position: 'bottom' }} type="category" allowDuplicatedCategory={false}
                                                    tickFormatter={(unixTime) => inDay() ? moment(unixTime, 'YYYY-MM-DD HH:mm').format('HH:mm') : moment(unixTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD(HH:mm)')}
                                                    dataKey="unixTime" tick={{ fontSize: 12 }} />
                                                <YAxis tick={{ fontSize: 12 }}>
                                                    <Label value={`${factors[selectColumn]} (${factors_unit[selectColumn]})`} angle={270} offset={0} position="center" />
                                                </YAxis>
                                                <Tooltip labelFormatter={(value, name, props) => ('日期 : ' + moment(value, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss'))}
                                                    formatter={(value, name, props) => (Math.round(value * 100) / 100 + ' ')} />

                                                {Object.keys(smartData).map((item, index) => {
                                                    let itemName = item;
                                                    let inverter = inverters.find(x => x.IndexID === item);
                                                    if (inverter) itemName = `${inverter.IndexID} - ${inverter.Name} (${inverter.KW} KW)`;
                                                    console.log(smartData[item])
                                                    return (
                                                        <Line key={index} name={itemName} unit={ factors_unit[selectColumn]} data={smartData[item]} connectNulls 
                                                            type="monotone" dot={false} dataKey="VALUE" stackId={index} stroke={colorMap[index]} strokeWidth={1.5} />)
                                                })}
                                            </ComposedChart >
                                        </ResponsiveContainer>
                                    </>
                                    : <Box textAlign="center" color="red" fontSize="h5.fontSize">沒有相關資料</Box>
                                }

                            </div>
                            : <Box pt={15}>
                                {smartQuery && smartQuery.status === 1 ?
                                    <LinearProgress />
                                    : smartQuery && smartQuery.status === 3 ?
                                        <Box textAlign="center" color="red" fontSize="h5.fontSize">{smartQuery.error}</Box>
                                        : null}
                            </Box>
                        :
                        <Box textAlign="center" color="red" fontSize="h5.fontSize">{stationList.error}</Box>
                    }
                </Grid>
            </Grid>
        </Box>
    );
};

export default SmartQuery;